import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Employee, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot, writeAuditLog } from '@shared/utils';

// 電子印は https:// のリモートURL、またはアップロード画像の data:image/ URL（Base64）を許可する。
const STAMP_URL_PATTERN = /^(https:\/\/|data:image\/)/;
const STAMP_URL_MESSAGE = 'stampUrl は https:// または data:image/ スキームのみ許可されています。';

function isAllowedStampUrl(value: string): boolean {
  return STAMP_URL_PATTERN.test(value);
}

const employeeCreateSchema = z.object({
  name: z.string().min(1, '社員名は必須です'),
  furi: z.string().min(1, 'フリガナは必須です'),
  tel: z.string().default(''),
  email: z.string().email('メールアドレスの形式が正しくありません').or(z.literal('')).default(''),
  categoryId: z.string().default(''),
  usergroupId: z.string().default(''),
  stampUrl: z.union([
    z.string().refine(isAllowedStampUrl, { message: STAMP_URL_MESSAGE }),
    z.null(),
  ]).default(null),
  comment: z.string().default(''),
  priority: z.number().default(0),
});

export type EmployeeCreateInput = z.infer<typeof employeeCreateSchema>;
export type EmployeeUpdateInput = Partial<EmployeeCreateInput>;

const COL = 'employees';

export async function fetchEmployees(filters?: {
  usergroupId?: string;
  categoryId?: string;
}): Promise<Employee[]> {
  let q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    orderBy('priority')
  );
  if (filters?.usergroupId) {
    q = query(q, where('usergroupId', '==', filters.usergroupId));
  }
  if (filters?.categoryId) {
    q = query(q, where('categoryId', '==', filters.categoryId));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Employee);
}

export async function fetchEmployee(id: ID): Promise<Employee | null> {
  const snap = await getDoc(doc(db, COL, id));
  return fromSnapshot<Employee>(snap);
}

export async function createEmployee(
  data: EmployeeCreateInput,
  userId: ID
): Promise<Employee> {
  const validated = parseOrThrow(employeeCreateSchema, data);
  const payload = { ...validated, ...buildBaseFields(userId) };
  const ref = await addDoc(collection(db, COL), payload);
  const created = { id: ref.id, ...payload } as unknown as Employee;
  await writeAuditLog({
    entityType: 'employee',
    entityId: ref.id,
    action: 'create',
    userId,
    after: validated,
  });
  return created;
}

export async function updateEmployee(
  id: ID,
  data: EmployeeUpdateInput,
  userId: ID
): Promise<Employee> {
  const validated = employeeCreateSchema.partial().parse(data);
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('社員が見つかりません。');
  const before = snap.data();
  const patch = { ...validated, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'employee',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: validated,
  });
  return { id, ...before, ...patch } as unknown as Employee;
}

export async function deleteEmployee(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('社員が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'employee',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
  });
}

export async function updateEmployeeStamp(
  id: ID,
  stampUrl: string | null,
  userId: ID
): Promise<void> {
  if (stampUrl !== null && !isAllowedStampUrl(stampUrl)) {
    throw new Error(STAMP_URL_MESSAGE);
  }
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('社員が見つかりません。');
  const before = snap.data();
  await updateDoc(ref, { stampUrl, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'employee',
    entityId: id,
    action: 'update',
    userId,
    before: { stampUrl: before['stampUrl'] },
    after: { stampUrl },
  });
}
